import { QuestionElement } from '#components/QuestionElement';
import { useThemeContext } from '#contexts/ThemeContext';
import { Dictionary, EFieldType, Field, PolygonType, QuestionnaireElements } from '#models/questionnaire';
import { AddressField } from '#modules/LeadGeneration/components/AddressField';
import { CheckBoxField } from '#modules/LeadGeneration/components/CheckBoxField';
import { DatePickerField } from '#modules/LeadGeneration/components/DatePickerField';
import { DropdownField } from '#modules/LeadGeneration/components/DropdownField';
import { TextField } from '#modules/LeadGeneration/components/TextField';
import { TimePickerField } from '#modules/LeadGeneration/components/TimePickerField/TimePickerField';
import { Box, Button, Grid, makeStyles } from '@material-ui/core';
import { boxShadow, colors } from '@swyft/ui';
import { useFormikContext } from 'formik';
import { sortBy } from 'lodash-es';
import * as React from 'react';

export interface LeadGenerationLayoutProps {
  fields: Field[];
  handleSelectAnswer: (value: any) => void;
  buttonSubmitText: string | null;
  handleChangeFields: (fieldKey: string, value: any, addressLocation?: any) => void;
  questionLocation: PolygonType[][];
  isMapEnabled: boolean;
  title: string;
}

export const LeadGenerationLayout = ({
  fields,
  buttonSubmitText,
  handleChangeFields,
  questionLocation,
  isMapEnabled,
  title
}: LeadGenerationLayoutProps) => {
  const { handleSubmit, isSubmitting, errors, touched } = useFormikContext();
  const elements = useThemeContext();
  const classes = useStyles(elements)();

  const locationError = React.useMemo(() => {
    if (errors) {
      const questionFieldSubmissionsError = (errors as Dictionary).questionFieldSubmissions || [];
      if (questionFieldSubmissionsError && questionFieldSubmissionsError.length) {
        return questionFieldSubmissionsError.some(
          (item: Dictionary) => item && item.location === 'Sorry, our service is not available in your area'
        );
      }
    }
    return false;
  }, [errors]);

  return (
    <Box
      width={{ xs: '100%', sm: '80%', md: '85%', lg: '90%' }}
      maxWidth={{ xs: 350, sm: 525, md: 678, lg: 919, xl: 1171 }}
      pb={8}
      mt={3}
      mb={{ xs: 6, sm: 3 }}
      mx='auto'
      boxShadow={boxShadow}
      bgcolor={colors.white}
      borderRadius={4}
    >
      <QuestionElement question={title} suggestion={''} />
      <Grid container spacing={2}>
        <Grid item xs />
        <Grid item xs={10} sm={8} md={8} lg={7}>
          <Grid container direction='row' justifyContent='flex-start' alignItems='stretch' spacing={3}>
            {sortBy(fields, ({ type }) => (type !== EFieldType.Checkbox ? 0 : 1), 'sortOrder').map(
              (field: Field, index: number) => (
                <Grid
                  item
                  key={index}
                  xs={12}
                  md={EFieldType.Textarea === field.type ? 12 : EFieldType.Checkbox === field.type ? 10 : 6}
                  style={{
                    margin: EFieldType.Checkbox === field.type ? '0 auto' : 0
                  }}
                >
                  {field.type === EFieldType.Email && (
                    <TextField field={field} handleChangeFields={handleChangeFields} />
                  )}
                  {field.type === EFieldType.PhoneNumber && (
                    <TextField field={field} handleChangeFields={handleChangeFields} />
                  )}
                  {field.type === EFieldType.Address && !isMapEnabled && (
                    <TextField field={field} handleChangeFields={handleChangeFields} />
                  )}
                  {field.type === EFieldType.Address && isMapEnabled && (
                    <AddressField
                      field={field}
                      handleChangeFields={handleChangeFields}
                      questionLocation={questionLocation}
                    />
                  )}
                  {field.type === EFieldType.TextBox && (
                    <TextField field={field} handleChangeFields={handleChangeFields} />
                  )}
                  {field.type === EFieldType.CompanyName && (
                    <TextField field={field} handleChangeFields={handleChangeFields} />
                  )}
                  {field.type === EFieldType.CompanyURL && (
                    <TextField field={field} handleChangeFields={handleChangeFields} />
                  )}
                  {field.type === EFieldType.Dropdown && (
                    <DropdownField field={field} handleChangeFields={handleChangeFields} />
                  )}
                  {field.type === EFieldType.DatePicker && (
                    <DatePickerField field={field} handleChangeFields={handleChangeFields} />
                  )}
                  {field.type === EFieldType.TimePicker && (
                    <TimePickerField field={field} handleChangeFields={handleChangeFields} />
                  )}
                  {field.type === EFieldType.Checkbox && (
                    <CheckBoxField field={field} handleChangeFields={handleChangeFields} />
                  )}
                  {field.type === EFieldType.FirstName && (
                    <TextField field={field} handleChangeFields={handleChangeFields} />
                  )}
                  {field.type === EFieldType.LastName && (
                    <TextField field={field} handleChangeFields={handleChangeFields} />
                  )}
                  {field.type === EFieldType.Textarea && (
                    <TextField field={field} multiline={true} handleChangeFields={handleChangeFields} />
                  )}
                  {field.type === EFieldType.Discount && (
                    <TextField field={field} handleChangeFields={handleChangeFields} disabled={true} />
                  )}
                </Grid>
              )
            )}
            <Grid item xs={12}>
              <Box
                width={{
                  xs: '100%'
                }}
                maxWidth={350}
                mx='auto'
                display='flex'
                justifyContent='center'
                height={46}
              >
                <Button
                  disabled={isSubmitting || locationError}
                  fullWidth
                  className={classes.button}
                  variant='contained'
                  onClick={() => handleSubmit()}
                >
                  {buttonSubmitText ? buttonSubmitText : 'Book Now'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs />
      </Grid>
    </Box>
  );
};

const useStyles = ({ primaryButtonBackground, primaryButtonText }: Partial<QuestionnaireElements>) =>
  makeStyles(() => ({
    button: {
      backgroundColor: primaryButtonBackground,
      color: primaryButtonText,
      '& span': {
        color: `${primaryButtonText}!important`
      },
      '&:hover': {
        backgroundColor: primaryButtonBackground,
        opacity: 0.8
      }
    }
  }));
